/*

? The chat's "Send location" dialog (18 Oct 2026, the app's ChatLocationSheet; restyled 19 Oct 2026
? toward the app's LocationPicker): a map preview with a pin on where the viewer is, "Send your
? current location" with how accurate the browser says it is, a place search (Google Places, the
? property search's hooks), and the places this viewer picked before.

  Picking any of them sends it at once through the parent's [onSend], with its address line; the
  server writes the message (chat/send-message `location`, lib/chat/locationContact.ts).

  - Recent places, not nearby ones (decided 19 Oct 2026): every place picked from the search or the
    recent list is kept in this browser's localStorage (newest first, at most RECENT_PLACES_MAX), like
    recent searches. Storage can be missing or refuse (a private window), so every read and write is
    wrapped and the dialog works without it. The viewer's own position is never stored.
  - Location access refused: the map and the current-location row give way to "Location access is
    off" with how to allow it in the browser and Try again; the search and recent places still work.

*/

/* Common Imports */

import { styled, useTheme } from "@mui/system";
import React from "react";

/* Component Imports */

import { Autocomplete, CircularProgress, Dialog, DialogContent, IconButton, TextField, Typography } from "@mui/material";
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";
import BeegruButton from "../../../common-components/buttons/BeegruButton";
import { ChatMapPreview, useChatMapsLoader } from "./ChatMaps";

/* Icon Imports */

import { Close, LocationOffOutlined, LocationOnOutlined, Refresh } from "@mui/icons-material";

/** What a picked place sends: the server writes "<title>\n<address>\n<maps link>". */
export type SharedPlace = { latitude: number; longitude: number; title: string; address?: string };

/* The recent places' storage key and how many are kept. */
const RECENT_PLACES_KEY: string = "beegru_chat_recent_places";
const RECENT_PLACES_MAX: number = 8;

/** The recent places in this browser, newest first; [] when there are none or storage is unavailable. */
const readRecentPlaces = (): Array<SharedPlace> => {
	try {
		const raw: string | null = window.localStorage.getItem(RECENT_PLACES_KEY);
		const parsed: any = raw ? JSON.parse(raw) : [];
		return Array.isArray(parsed)
			? parsed
					.filter(
						(place: any) =>
							place && typeof place.title === "string" && Number.isFinite(place.latitude) && Number.isFinite(place.longitude),
					)
					.slice(0, RECENT_PLACES_MAX)
			: [];
	} catch {
		return [];
	}
};

/** Puts [place] first in the recent places (once), keeping at most RECENT_PLACES_MAX. Never throws. */
const rememberPlace = (place: SharedPlace): void => {
	try {
		const sameAs = (other: SharedPlace): boolean =>
			other.title === place.title &&
			Math.abs(other.latitude - place.latitude) < 0.0001 &&
			Math.abs(other.longitude - place.longitude) < 0.0001;
		const next: Array<SharedPlace> = [place, ...readRecentPlaces().filter((other) => !sameAs(other))].slice(0, RECENT_PLACES_MAX);
		window.localStorage.setItem(RECENT_PLACES_KEY, JSON.stringify(next));
	} catch {
		// Storage is unavailable or full: the place is sent all the same.
	}
};

/* Styled Components */

const HeaderRow = styled("div")({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	gap: "0.25rem",
	padding: "0.75rem 0.75rem 0.75rem 1.25rem",
});

const PlaceRow = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	gap: "0.75rem",
	padding: "0.625rem 1.25rem",
	cursor: "pointer",
	"&:hover": {
		background: theme.palette.background.highlight,
	},
}));

/* The round glyph in front of a place row. */
const PlaceGlyph = styled("div")(({ theme }) => ({
	width: "2.25rem",
	height: "2.25rem",
	borderRadius: "50%",
	flexShrink: 0,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	background: theme.palette.background.highlight,
	color: theme.palette.primary.dark,
}));

/* "Send your current location": a filled dot in a ring, green like the app's. */
const CurrentDot = styled("div")(({ theme }) => ({
	width: "2.25rem",
	height: "2.25rem",
	borderRadius: "50%",
	flexShrink: 0,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	border: `2px solid ${theme.palette.success.main}`,
	"&::after": {
		content: '""',
		width: "1.125rem",
		height: "1.125rem",
		borderRadius: "50%",
		background: theme.palette.success.main,
	},
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
	padding: "0.75rem 1.25rem 0.25rem",
	fontSize: "0.75rem",
	fontWeight: 500,
	color: theme.palette.text.secondary,
}));

const OneLine = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } as const;

/* Where the browser put the viewer, and how sure it is (metres). */
type Position = { latitude: number; longitude: number; accuracy: number };

const ChatLocationDialog = ({
	open,
	onClose,
	onSend,
	locale,
}: {
	open: boolean;
	onClose: () => void;
	/* Sends the place; the parent closes the dialog. */
	onSend: (place: SharedPlace) => void;
	locale?: string;
}) => {
	const theme: any = useTheme();
	const ar: boolean = locale === "ar-AE";
	const kn: boolean = locale === "kn-IN";
	const lang = {
		title: ar ? "إرسال موقع" : kn ? "ಸ್ಥಳವನ್ನು ಕಳುಹಿಸಿ" : "Send location",
		current: ar ? "إرسال موقعك الحالي" : kn ? "ನಿಮ್ಮ ಪ್ರಸ್ತುತ ಸ್ಥಳವನ್ನು ಕಳುಹಿಸಿ" : "Send your current location",
		accuracy: (metres: number): string =>
			ar ? `بدقة ${metres} متر` : kn ? `${metres} ಮೀಟರ್‌ವರೆಗೆ ನಿಖರ` : `Accurate to ${metres} metres`,
		finding: ar ? "جارٍ تحديد موقعك…" : kn ? "ನಿಮ್ಮ ಸ್ಥಳವನ್ನು ಹುಡುಕಲಾಗುತ್ತಿದೆ…" : "Finding your location…",
		search: ar ? "ابحث عن مكان" : kn ? "ಸ್ಥಳವನ್ನು ಹುಡುಕಿ" : "Search a place, e.g. HSR Layout, Bengaluru",
		failed: ar
			? "تعذر العثور على موقعك. يرجى المحاولة مرة أخرى."
			: kn
				? "ನಿಮ್ಮ ಸ್ಥಳವನ್ನು ಕಂಡುಹಿಡಿಯಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ."
				: "Couldn't find your location. Please try again.",
		place_failed: ar
			? "تعذر مشاركة هذا المكان. يرجى المحاولة مرة أخرى."
			: kn
				? "ಆ ಸ್ಥಳವನ್ನು ಹಂಚಿಕೊಳ್ಳಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ."
				: "Couldn't share that place. Please try again.",
		current_title: ar ? "الموقع الحالي" : kn ? "ಪ್ರಸ್ತುತ ಸ್ಥಳ" : "Current location",
		off_title: ar ? "الوصول إلى الموقع متوقف" : kn ? "ಸ್ಥಳದ ಪ್ರವೇಶ ಆಫ್ ಆಗಿದೆ" : "Location access is off",
		off_body: ar
			? "اسمح بالوصول إلى الموقع لهذا الموقع من إعدادات المتصفح (الرمز بجانب شريط العنوان)، ثم حاول مرة أخرى. أو ابحث عن مكان أدناه."
			: kn
				? "ಬ್ರೌಸರ್ ಸೆಟ್ಟಿಂಗ್‌ಗಳಲ್ಲಿ (ವಿಳಾಸ ಪಟ್ಟಿಯ ಪಕ್ಕದ ಐಕಾನ್) ಈ ಸೈಟ್‌ಗೆ ಸ್ಥಳವನ್ನು ಅನುಮತಿಸಿ, ನಂತರ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ. ಅಥವಾ ಕೆಳಗೆ ಸ್ಥಳವನ್ನು ಹುಡುಕಿ."
				: "Allow location for this site in your browser's settings (the icon beside the address bar), then try again. Or search for a place below.",
		try_again: ar ? "حاول مرة أخرى" : kn ? "ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ" : "Try again",
		recent: ar ? "الأماكن الأخيرة" : kn ? "ಇತ್ತೀಚಿನ ಸ್ಥಳಗಳು" : "Recent places",
		refresh: ar ? "تحديث الموقع" : kn ? "ಸ್ಥಳವನ್ನು ರಿಫ್ರೆಶ್ ಮಾಡಿ" : "Refresh location",
		close: ar ? "إغلاق" : kn ? "ಮುಚ್ಚಿ" : "Close",
	};

	/* --- Search (Google Places) --- */

	const { isLoaded } = useChatMapsLoader();
	const {
		value,
		suggestions: { data, loading },
		setValue,
		clearSuggestions,
		init,
	} = usePlacesAutocomplete({ initOnMount: false, debounce: 400 });

	React.useEffect(() => {
		if (isLoaded) init();
	}, [isLoaded, init]);

	/* --- The viewer's position --- */

	const [position, setPosition] = React.useState<Position | null>(null);
	const [locating, setLocating] = React.useState<boolean>(false);
	/* "denied": the browser refuses location for this site; "failed": it couldn't find one this time. */
	const [locationProblem, setLocationProblem] = React.useState<"" | "denied" | "failed">("");
	const [placeFailed, setPlaceFailed] = React.useState<boolean>(false);
	const [sending, setSending] = React.useState<boolean>(false);
	const [recentPlaces, setRecentPlaces] = React.useState<Array<SharedPlace>>([]);
	/* Each locate() takes a number, so an answer that arrives after a newer one began is ignored. */
	const locateRun = React.useRef<number>(0);

	/** Asks the browser where the viewer is; resolves with the position, or null when it can't say. */
	const locate = (): Promise<Position | null> =>
		new Promise((resolve) => {
			if (typeof navigator === "undefined" || !navigator.geolocation) {
				setLocationProblem("failed");
				resolve(null);
				return;
			}
			const run: number = ++locateRun.current;
			setLocating(true);
			setLocationProblem("");
			navigator.geolocation.getCurrentPosition(
				(found) => {
					if (run !== locateRun.current) return resolve(null);
					const next: Position = {
						latitude: found.coords.latitude,
						longitude: found.coords.longitude,
						accuracy: Math.max(1, Math.round(found.coords.accuracy || 0)),
					};
					setLocating(false);
					setPosition(next);
					resolve(next);
				},
				(error) => {
					if (run !== locateRun.current) return resolve(null);
					setLocating(false);
					setLocationProblem(error && error.code === 1 ? "denied" : "failed");
					resolve(null);
				},
				{ enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
			);
		});

	// A fresh dialog on every opening: it starts finding the viewer straight away, for the map.
	React.useEffect(() => {
		if (open) {
			setValue("", false);
			clearSuggestions();
			setPosition(null);
			setPlaceFailed(false);
			setSending(false);
			setRecentPlaces(readRecentPlaces());
			locate();
		} else {
			// A position that arrives after closing is dropped.
			locateRun.current += 1;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	/* --- Sending --- */

	/** The street address for [latitude], [longitude], or "" when it can't be read. */
	const addressAt = async (latitude: number, longitude: number): Promise<string> => {
		if (!isLoaded) return "";
		try {
			const results = await getGeocode({ location: { lat: latitude, lng: longitude } });
			return String(results?.[0]?.formatted_address || "");
		} catch {
			return "";
		}
	};

	const sendCurrentLocation = async () => {
		if (sending || locating) return;
		const here: Position | null = position || (await locate());
		if (!here) return;
		setSending(true);
		const address: string = await addressAt(here.latitude, here.longitude);
		onSend({ latitude: here.latitude, longitude: here.longitude, title: lang.current_title, address });
	};

	/** Sends a picked place and keeps it among the recent places. */
	const sendPlace = (place: SharedPlace) => {
		if (sending) return;
		setSending(true);
		rememberPlace(place);
		onSend(place);
	};

	/** A search suggestion: its coordinates come from Places by its id. */
	const sendSuggestion = async (suggestion: any) => {
		if (sending) return;
		setPlaceFailed(false);
		try {
			const results = await getGeocode({ placeId: suggestion.place_id });
			const { lat, lng } = getLatLng(results[0]);
			sendPlace({
				latitude: lat,
				longitude: lng,
				title: String(suggestion.structured_formatting?.main_text || suggestion.description || ""),
				address: String(suggestion.structured_formatting?.secondary_text || results[0]?.formatted_address || ""),
			});
		} catch {
			setPlaceFailed(true);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="xs"
			dir={ar ? "rtl" : "ltr"}
			PaperProps={{ sx: { borderRadius: "1rem", overflow: "hidden" } }}
		>
			<HeaderRow>
				<Typography sx={{ fontWeight: 600, fontSize: "1.0625rem", flex: 1 }}>{lang.title}</Typography>
				<IconButton
					size="small"
					onClick={() => locate()}
					disabled={locating}
					aria-label={lang.refresh}
				>
					<Refresh fontSize="small" />
				</IconButton>
				<IconButton
					size="small"
					onClick={onClose}
					aria-label={lang.close}
				>
					<Close fontSize="small" />
				</IconButton>
			</HeaderRow>
			<DialogContent sx={{ padding: "0rem 0rem 1rem", display: "flex", flexDirection: "column" }}>
				{locationProblem === "denied" ? (
					/* The browser refuses location here: say how to allow it; search and recents stay. */
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: "0.5rem",
							padding: "1.5rem 1.5rem 1rem",
							textAlign: "center",
						}}
					>
						<LocationOffOutlined sx={{ fontSize: "2rem", color: "text.secondary" }} />
						<Typography sx={{ fontWeight: 600, fontSize: "0.9375rem" }}>{lang.off_title}</Typography>
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ fontSize: "0.8125rem" }}
						>
							{lang.off_body}
						</Typography>
						<BeegruButton
							flavor="primary"
							variant="contained"
							onClick={() => locate()}
							sx={{ textTransform: "none", marginTop: "0.25rem" }}
						>
							{lang.try_again}
						</BeegruButton>
					</div>
				) : (
					<React.Fragment>
						<ChatMapPreview
							latitude={position ? position.latitude : null}
							longitude={position ? position.longitude : null}
							height="11rem"
							zoom={16}
						/>
						<PlaceRow
							role="button"
							tabIndex={0}
							onClick={sendCurrentLocation}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") sendCurrentLocation();
							}}
							sx={{ cursor: locating || sending ? "default" : "pointer" }}
						>
							{locating || sending ? (
								<PlaceGlyph>
									<CircularProgress size={18} />
								</PlaceGlyph>
							) : (
								<CurrentDot />
							)}
							<div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
								<Typography sx={{ fontWeight: 600, fontSize: "0.9375rem" }}>{lang.current}</Typography>
								<Typography
									variant="body2"
									sx={{ color: locationProblem === "failed" ? "warning.dark" : "text.secondary", fontSize: "0.8125rem" }}
								>
									{locating
										? lang.finding
										: locationProblem === "failed"
											? lang.failed
											: position
												? lang.accuracy(position.accuracy)
												: lang.finding}
								</Typography>
							</div>
						</PlaceRow>
					</React.Fragment>
				)}
				<div style={{ padding: "0.5rem 1.25rem 0rem", borderTop: `1px solid ${theme.palette.divider}` }}>
					<Autocomplete
						freeSolo
						disabled={!isLoaded || sending}
						options={data || []}
						loading={loading}
						inputValue={value}
						onInputChange={(_, next) => setValue(next)}
						getOptionLabel={(option: any) => (typeof option === "string" ? option : String(option?.description || ""))}
						onChange={(_, picked: any) => {
							if (picked && typeof picked === "object") sendSuggestion(picked);
						}}
						filterOptions={(options) => options}
						renderOption={(props, option: any) => {
							const { key, ...rest } = props as any;
							return (
								<li
									key={option.place_id || key}
									{...rest}
								>
									<div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
										<Typography
											variant="body2"
											sx={{ fontWeight: 500, ...OneLine }}
										>
											{option.structured_formatting?.main_text || option.description}
										</Typography>
										{option.structured_formatting?.secondary_text ? (
											<Typography
												variant="caption"
												color="text.secondary"
												sx={OneLine}
											>
												{option.structured_formatting.secondary_text}
											</Typography>
										) : null}
									</div>
								</li>
							);
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								placeholder={lang.search}
								size="small"
							/>
						)}
					/>
					{placeFailed ? (
						<Typography
							variant="body2"
							sx={{ color: "warning.dark", fontSize: "0.8125rem", marginTop: "0.5rem" }}
						>
							{lang.place_failed}
						</Typography>
					) : null}
				</div>
				{recentPlaces.length > 0 ? (
					<React.Fragment>
						<SectionLabel>{lang.recent}</SectionLabel>
						{recentPlaces.map((place: SharedPlace, index: number) => (
							<PlaceRow
								key={`${place.title}-${place.latitude}-${place.longitude}-${index}`}
								role="button"
								tabIndex={0}
								onClick={() => sendPlace(place)}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") sendPlace(place);
								}}
							>
								<PlaceGlyph>
									<LocationOnOutlined sx={{ fontSize: "1.125rem" }} />
								</PlaceGlyph>
								<div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
									<Typography
										variant="body2"
										sx={{ fontWeight: 600, ...OneLine }}
									>
										{place.title}
									</Typography>
									{place.address ? (
										<Typography
											variant="caption"
											color="text.secondary"
											sx={OneLine}
										>
											{place.address}
										</Typography>
									) : null}
								</div>
							</PlaceRow>
						))}
					</React.Fragment>
				) : null}
			</DialogContent>
		</Dialog>
	);
};

export default ChatLocationDialog;
